/**
 * Microphone capture producing raw 16-bit PCM for realtime transcription.
 *
 * `MediaRecorder` is deliberately not used here: it emits container formats
 * (WebM/Opus on Chrome, MP4/AAC on Safari) that the realtime model cannot
 * consume, and its output is only delivered in chunks after the fact. Reading
 * the Web Audio graph directly gives us uncompressed mono samples on every
 * platform, including iOS, at the cadence the socket needs.
 */

/**
 * Samples per outgoing chunk: 50ms at 16 kHz. Every chunk is audio the model
 * has not seen yet, so this bounds how far capture lags the speaker. Halving it
 * from 100ms removes 50ms of buffering for ~40 KB/s of upload.
 */
const SAMPLES_PER_CHUNK = 800;

/** Frames handed to a worklet per call; also the ScriptProcessor buffer size. */
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;

/** Cap on the shutdown flush handshake, so a wedged worklet cannot hang stop(). */
const FLUSH_TIMEOUT_MS = 100;

export type PcmRecorderOptions = {
  /** Target sample rate. Audio is downsampled from the device's native rate. */
  sampleRate: number;
  /** Receives each chunk of little-endian 16-bit mono PCM. */
  onAudio: (chunk: ArrayBuffer) => void;
};

/** A compressed copy of the recording, when the browser could produce one. */
export type CompressedRecording = {
  blob: Blob;
  /** The container the browser actually produced, never assumed. */
  mimeType: string;
};

export type PcmRecorder = {
  /**
   * The live capture stream. Exposed so a visualizer can read levels from the
   * same microphone instead of opening a second one.
   */
  stream: MediaStream;
  /** Stop capture and release the microphone. Safe to call more than once. */
  stop: () => Promise<void>;
  /**
   * The recording as compressed audio, available once {@link stop} resolves.
   *
   * The realtime path needs uncompressed PCM, but uploading that same PCM for
   * the final pass would mean 3.7 MB for two minutes and 9 MB for five — slow
   * and expensive on mobile data. A parallel `MediaRecorder` on the same stream
   * yields the same audio around six times smaller, at no extra capture cost.
   * Null when the browser has no usable encoder, in which case the caller falls
   * back to sending the PCM it already holds.
   */
  getCompressed: () => CompressedRecording | null;
};

export type MicrophoneErrorReason = "permission-denied" | "unavailable";

/** Microphone capture failed in a way the UI needs to distinguish. */
export class MicrophoneError extends Error {
  /** Declared explicitly: the build targets a lib without `Error.cause`. */
  readonly cause?: unknown;

  constructor(readonly reason: MicrophoneErrorReason, cause?: unknown) {
    super(`Microphone unavailable: ${reason}`);
    this.name = "MicrophoneError";
    this.cause = cause;
  }
}

/**
 * Worklet source, inlined and loaded from a blob URL.
 *
 * Keeping it inline rather than as a separate asset means it survives every
 * bundling and deployment layout unchanged — a worklet fetched by URL breaks
 * as soon as the app is served from a different base path or origin.
 */
const WORKLET_SOURCE = `
class PcmDownsampler extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const { targetSampleRate, samplesPerChunk } = options.processorOptions;
    // Input samples consumed per output sample; fractional rates are carried
    // across blocks so the stream never drifts.
    this.ratio = sampleRate / targetSampleRate;
    this.samplesPerChunk = samplesPerChunk;
    this.buffer = new Int16Array(samplesPerChunk);
    this.bufferIndex = 0;
    this.windowSum = 0;
    this.windowCount = 0;
    this.windowPosition = 0;
    this.isStopped = false;
    this.port.onmessage = (event) => {
      if (event.data === 'stop') {
        this.flush();
        this.isStopped = true;
        // Acknowledge so the main thread knows the tail chunk has been sent
        // and it is safe to tear the audio graph down.
        this.port.postMessage('flushed');
      }
    };
  }

  flush() {
    if (this.bufferIndex === 0) return;
    const chunk = this.buffer.slice(0, this.bufferIndex);
    this.port.postMessage(chunk.buffer, [chunk.buffer]);
    this.bufferIndex = 0;
  }

  pushSample(value) {
    const clamped = Math.max(-1, Math.min(1, value));
    // Asymmetric scaling: the negative range of int16 has one more step.
    this.buffer[this.bufferIndex++] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    if (this.bufferIndex === this.samplesPerChunk) {
      this.flush();
      this.buffer = new Int16Array(this.samplesPerChunk);
    }
  }

  process(inputs) {
    if (this.isStopped) return false;
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;

    for (let i = 0; i < channel.length; i += 1) {
      // Averaging every input sample that falls in an output window doubles as
      // a crude anti-aliasing filter, which plain decimation would skip.
      this.windowSum += channel[i];
      this.windowCount += 1;
      this.windowPosition += 1;
      if (this.windowPosition >= this.ratio) {
        this.pushSample(this.windowSum / this.windowCount);
        this.windowPosition -= this.ratio;
        this.windowSum = 0;
        this.windowCount = 0;
      }
    }
    return true;
  }
}

registerProcessor('pcm-downsampler', PcmDownsampler);
`;

let workletUrl: string | null = null;

const getWorkletUrl = (): string => {
  if (!workletUrl) {
    workletUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
    );
  }
  return workletUrl;
};

/**
 * Containers tried in order for the compressed copy. Chrome and Firefox take
 * the first, Safari the MP4 one; the empty string lets the browser pick if it
 * supports none of them explicitly.
 */
const COMPRESSED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "",
];

/** Opus at 32 kbps is transparent for speech and ~6x smaller than 16 kHz PCM. */
const COMPRESSED_BITS_PER_SECOND = 32_000;

/**
 * Constraints that decide whether a quiet speaker in an open-plan office is
 * transcribed correctly or not. Left to the browser's defaults, gain and noise
 * handling vary wildly between devices.
 */
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const requestMicrophone = async (): Promise<MediaStream> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new MicrophoneError("unavailable");
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: AUDIO_CONSTRAINTS,
    });
  } catch (error) {
    const isDenied =
      error instanceof DOMException &&
      (error.name === "NotAllowedError" || error.name === "SecurityError");
    throw new MicrophoneError(isDenied ? "permission-denied" : "unavailable", error);
  }
};

/**
 * Start capturing the microphone as PCM.
 *
 * Must be called from a user gesture: iOS only lets an `AudioContext` leave the
 * suspended state inside one, and a suspended context silently produces no audio.
 */
export const startPcmRecorder = async (
  options: PcmRecorderOptions,
): Promise<PcmRecorder> => {
  const stream = await requestMicrophone();

  let audioContext: AudioContext;
  try {
    audioContext = new AudioContext();
    // Safari hands back a suspended context even inside a gesture.
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
  } catch (error) {
    stopStream(stream);
    throw new MicrophoneError("unavailable", error);
  }

  const source = audioContext.createMediaStreamSource(stream);
  // A muted sink keeps the graph pulling without routing the microphone to the
  // speakers; nodes that reach no destination are not guaranteed to run.
  const sink = audioContext.createGain();
  sink.gain.value = 0;
  sink.connect(audioContext.destination);

  const teardown = await (audioContext.audioWorklet
    ? startWorkletCapture(audioContext, source, sink, options)
    : startScriptProcessorCapture(audioContext, source, sink, options));

  const compressed = startCompressedRecording(stream);
  let compressedRecording: CompressedRecording | null = null;

  let isStopped = false;
  const stop = async (): Promise<void> => {
    if (isStopped) return;
    isStopped = true;
    await teardown();

    // Let the encoder emit its final blob before the tracks are stopped,
    // otherwise the tail of the recording is lost.
    if (compressed && compressed.recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        compressed.recorder.addEventListener("stop", () => resolve(), {
          once: true,
        });
        compressed.recorder.stop();
      });
      if (compressed.chunks.length > 0) {
        const mimeType =
          compressed.recorder.mimeType || compressed.chunks[0].type;
        // Some browsers hand back a `video/*` container even for an audio-only
        // recording. The API only accepts `audio/*`, and would drop it without
        // a word, so the caller is better off uploading the PCM it already has.
        if (mimeType.startsWith("audio/")) {
          compressedRecording = {
            blob: new Blob(compressed.chunks, { type: mimeType }),
            mimeType,
          };
        }
      }
    }

    source.disconnect();
    sink.disconnect();
    stopStream(stream);
    await audioContext.close().catch(() => undefined);
  };

  return { stream, stop, getCompressed: () => compressedRecording };
};

/**
 * Start a compressed recording alongside the PCM capture.
 *
 * Returns null when `MediaRecorder` is unavailable or refuses every container,
 * which simply means the caller uploads PCM instead.
 */
const startCompressedRecording = (
  stream: MediaStream,
): { recorder: MediaRecorder; chunks: Blob[] } | null => {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }

  const mimeType = COMPRESSED_MIME_TYPES.find(
    (candidate) =>
      candidate === "" || MediaRecorder.isTypeSupported?.(candidate),
  );
  if (mimeType === undefined) {
    return null;
  }

  try {
    const recorder = new MediaRecorder(stream, {
      ...(mimeType && { mimeType }),
      audioBitsPerSecond: COMPRESSED_BITS_PER_SECOND,
    });
    const chunks: Blob[] = [];
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    });
    recorder.start();
    return { recorder, chunks };
  } catch {
    // An unusable encoder is not a reason to lose the dictation.
    return null;
  }
};

const stopStream = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) {
    track.stop();
  }
};

/** Preferred path: capture runs on the audio thread, immune to main-thread jank. */
const startWorkletCapture = async (
  audioContext: AudioContext,
  source: MediaStreamAudioSourceNode,
  sink: GainNode,
  options: PcmRecorderOptions,
): Promise<() => Promise<void>> => {
  await audioContext.audioWorklet.addModule(getWorkletUrl());

  const node = new AudioWorkletNode(audioContext, "pcm-downsampler", {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    processorOptions: {
      targetSampleRate: options.sampleRate,
      samplesPerChunk: SAMPLES_PER_CHUNK,
    },
  });

  node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    options.onAudio(event.data);
  };

  source.connect(node);
  node.connect(sink);

  return async () => {
    // Wait for the processor to hand back its final partial chunk before
    // tearing the graph down — closing the context first would discard it.
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, FLUSH_TIMEOUT_MS);
      node.port.onmessage = (event: MessageEvent<ArrayBuffer | "flushed">) => {
        if (event.data === "flushed") {
          clearTimeout(timeout);
          resolve();
          return;
        }
        options.onAudio(event.data);
      };
      node.port.postMessage("stop");
    });
    node.port.onmessage = null;
    node.disconnect();
  };
};

/**
 * Fallback for browsers without `AudioWorklet` (notably Safari before 14.1).
 * `ScriptProcessorNode` is deprecated and runs on the main thread, but it is
 * the only remaining way to read raw samples there.
 */
const startScriptProcessorCapture = (
  audioContext: AudioContext,
  source: MediaStreamAudioSourceNode,
  sink: GainNode,
  options: PcmRecorderOptions,
): Promise<() => Promise<void>> => {
  const processor = audioContext.createScriptProcessor(
    SCRIPT_PROCESSOR_BUFFER_SIZE,
    1,
    1,
  );

  const ratio = audioContext.sampleRate / options.sampleRate;
  let buffer = new Int16Array(SAMPLES_PER_CHUNK);
  let bufferIndex = 0;
  let windowSum = 0;
  let windowCount = 0;
  let windowPosition = 0;

  const flush = () => {
    if (bufferIndex === 0) return;
    options.onAudio(buffer.slice(0, bufferIndex).buffer);
    buffer = new Int16Array(SAMPLES_PER_CHUNK);
    bufferIndex = 0;
  };

  processor.onaudioprocess = (event) => {
    const channel = event.inputBuffer.getChannelData(0);
    for (let i = 0; i < channel.length; i += 1) {
      windowSum += channel[i];
      windowCount += 1;
      windowPosition += 1;
      if (windowPosition < ratio) continue;

      const value = Math.max(-1, Math.min(1, windowSum / windowCount));
      buffer[bufferIndex++] = value < 0 ? value * 0x8000 : value * 0x7fff;
      windowPosition -= ratio;
      windowSum = 0;
      windowCount = 0;
      if (bufferIndex === SAMPLES_PER_CHUNK) {
        flush();
      }
    }
  };

  source.connect(processor);
  processor.connect(sink);

  // Synchronous by nature: the flush happens on the main thread already.
  return Promise.resolve(async () => {
    flush();
    processor.onaudioprocess = null;
    processor.disconnect();
  });
};
